import { Task } from '../types/task';
import { parseISO, addSeconds, addHours } from 'date-fns';
import { getProjectColor } from './colorUtils'; // Import getProjectColor

// Helper function to convert hex to rgba
export const hexToRgba = (hex: string, alpha: number): string => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    console.warn(`[hexToRgba] Invalid hex color received: ${hex}, falling back to default gray.`);
    return 'rgba(72, 72, 72, 0.1)'; // Default fallback gray with alpha
  }
  const hexValue = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const bigint = parseInt(hexValue.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Shared interface for Calendar Events
export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color: string; // Still useful for text color or fallbacks
  description?: string;
  allDay?: boolean;
  task?: Task; // Reference to the original task
  textDecoration?: string;
  backgroundColor?: string; // Main background color control
  borderColor?: string; // Border color control
  classNames?: string[];
}

// Constants moved from Calendar.tsx
export const DEFAULT_EVENT_DURATION = 60 * 60; // 60 minutes in seconds
export const ALL_DAY_THRESHOLD = 24 * 60 * 60; // 24 hours in seconds

/**
 * Applies done opacity to a base color.
 * @param baseColor The base hex color string.
 * @param done Whether the task is done.
 * @returns A hex color string with opacity if done.
 */
export const applyDoneOpacity = (baseColor: string, done: boolean): string => {
  const opacity = done ? 'BF' : ''; // BF is hex for 75% opacity
  // Basic check to avoid appending opacity multiple times or to invalid strings
  if (baseColor.length > 7 && baseColor.endsWith(opacity)) {
      return baseColor; // Already has correct opacity
  }
  if (baseColor.length === 7) { // Standard #RRGGBB
      return `${baseColor}${opacity}`;
  }
  // Handle potential existing opacity or other formats cautiously
  // For now, just return baseColor if unsure, or implement more robust hex handling
  console.warn("[applyDoneOpacity] Received baseColor in unexpected format:", baseColor);
  return baseColor; // Fallback
};

/**
 * Converts a Task object into one or two CalendarEvent objects.
 * One event for the scheduled time (startAt + cost) and optionally one for the due date.
 * @param task The task to convert.
 * @returns An array of CalendarEvent objects.
 */
export const taskToEvents = (task: Task): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  const commonClassNames = [
    task.done ? 'task-done' : 'task-undone',
    task.vital ? 'task-vital' : '',
    task.urgent ? 'task-urgent' : '',
  ].filter(Boolean);

  // Use existing getProjectColor util, provide a default grey if no projectId
  const projectBaseColor = task.projectId ? getProjectColor(task.projectId) : '#8E8E93'; // iOS Gray

  // Calculate specific colors
  // Set background alpha to 0.1 for both done and not done states
  const eventBackgroundColor = hexToRgba(projectBaseColor, 0.1); // Always 10% opacity
  // Set border to transparent to remove default FullCalendar border
  const eventBorderColor = 'transparent';
  const eventTextColor = projectBaseColor; // Opaque project color for text/main color

  // 1. Main Event (Scheduled Time/Duration)
  if (task.startAt) {
    try {
      const start = parseISO(task.startAt);
      if (!isNaN(start.getTime())) { // Check if parsing was successful
        const effectiveCost = typeof task.cost === 'number' && task.cost > 0 ? task.cost : DEFAULT_EVENT_DURATION;
        const end = addSeconds(start, effectiveCost);
        const isAllDayEvent = !task.startAt.includes('T') && effectiveCost >= ALL_DAY_THRESHOLD;

        events.push({
          id: task.id,
          title: task.title,
          start,
          end,
          color: eventTextColor, // Opaque color for text/FC internal use
          description: task.notes,
          allDay: isAllDayEvent,
          task, // Keep reference to original task
          backgroundColor: eventBackgroundColor, // Specific background with 10% opacity
          borderColor: eventBorderColor, // Transparent border
          classNames: commonClassNames,
        });
      } else {
        console.warn(`Invalid startAt date format for task ${task.id}: ${task.startAt}`);
      }
    } catch (error) {
      console.error(`Error parsing startAt for task ${task.id}:`, error);
    }
  }

  // 2. Due Date Event (if applicable and distinct)
  if (task.dueAt) {
    try {
      const dueDate = parseISO(task.dueAt);
       if (!isNaN(dueDate.getTime())) { // Check if parsing was successful
         const isAllDayDue = !task.dueAt.includes('T'); // Treat date-only as all-day

         // Determine if the due date needs a separate visual representation
         let shouldShowDueEvent = true;
         const mainEvent = events.find(e => e.id === task.id);
         if (mainEvent) {
           // Don't show due event if it's effectively the same time as the main event's end
           const mainEventEndTimestamp = mainEvent.end.getTime();
           const dueTimestamp = dueDate.getTime();
           // Consider them the same if within a small threshold (e.g., 1 minute)
           if (Math.abs(mainEventEndTimestamp - dueTimestamp) < 60 * 1000) {
              // If main event is all day and due date is on the same day, don't show separate due event
             if (mainEvent.allDay && mainEvent.start.toDateString() === dueDate.toDateString()) {
                shouldShowDueEvent = false;
             } else if (!mainEvent.allDay) {
                shouldShowDueEvent = false; // If main event has time, and end matches due time
             }
           }
           // Also hide if main event end time is clearly after the due date time
           else if (mainEventEndTimestamp > dueTimestamp) {
             shouldShowDueEvent = false;
           }
         }

         if (shouldShowDueEvent) {
           // Due events also use project color, but add 'task-due' class
           // Keep background transparent for due markers
           const dueEventBackgroundColor = 'transparent';
           // Also make border transparent
           const dueEventBorderColor = 'transparent';
           const dueEventTextColor = projectBaseColor;
           // For all-day due dates, set start to beginning of day, end to end of day for full-day rendering
           const dueStart = isAllDayDue ? new Date(dueDate.setHours(0, 0, 0, 0)) : dueDate;
           // If not all day, give it a default duration (e.g., 1 hour) unless it would overlap badly
           const dueEnd = isAllDayDue ? new Date(dueDate.setHours(23, 59, 59, 999)) : addHours(dueDate, 1);

           events.push({
             id: `${task.id}-due`,
             title: task.title,
             start: dueStart,
             end: dueEnd,
             color: dueEventTextColor, // Opaque color
             description: task.notes,
             allDay: isAllDayDue,
             task, // Keep reference
             backgroundColor: dueEventBackgroundColor, // Specific background (transparent)
             borderColor: dueEventBorderColor, // Specific border (transparent)
             classNames: [...commonClassNames, 'task-due'],
           });
         }
      } else {
         console.warn(`Invalid dueAt date format for task ${task.id}: ${task.dueAt}`);
      }
    } catch (error) {
      console.error(`Error parsing dueAt for task ${task.id}:`, error);
    }
  }

  return events;
};